import React, { useState } from 'react'
import type { User, UpdateUserParams } from '../api/user'
import { adminApi } from '../api'
import { toastUtil } from '../../utils/toast'
import AdminInput from '../components/AdminInput'

interface AdminUserDetailModalProps {
  user: User
  onClose: () => void
  formatDate: (dateString: string) => string
  onUpdate?: () => void
  onDeleteSuccess?: () => void
}

const AdminUserDetailModal: React.FC<AdminUserDetailModalProps> = ({
  user,
  onClose,
  formatDate,
  onUpdate,
  onDeleteSuccess,
}) => {
  const [isEditMode, setIsEditMode] = useState(false)
  const [formData, setFormData] = useState<UpdateUserParams>({
    username: user.username,
    email: user.email || '',
    nickname: user.nickname,
    phone: user.phone || '',
    is_public: user.is_public,
    is_active: user.is_active,
    location_consent: user.location_consent,
    contact_consent: user.contact_consent,
    push_notification_consent: user.push_notification_consent,
  })
  const [isLoading, setIsLoading] = useState(false)

  const getSocialProviderName = (provider: string | null): string => {
    if (!provider) return ''
    switch (provider) {
      case 'google':
        return '구글'
      case 'kakao':
        return '카카오'
      case 'naver':
        return '네이버'
      default:
        return provider
    }
  }

  const getSocialProviderColor = (provider: string | null): string => {
    if (!provider) return ''
    switch (provider) {
      case 'google':
        return 'bg-white border border-black text-black hover:bg-gray-50'
      case 'kakao':
        return 'bg-[#FEE500] hover:bg-[#FDD835] text-black'
      case 'naver':
        return 'bg-[#03C75A] hover:bg-[#02B350] text-white'
      default:
        return 'bg-4B3621-80% hover:bg-4B3621-80%-70% text-white'
    }
  }

  const handleSubmit = async () => {
    if (!formData.nickname?.trim()) {
      toastUtil.error('닉네임을 입력해주세요')
      return
    }

    try {
      setIsLoading(true)
      await adminApi.updateUser(user.id, formData)
      toastUtil.success('사용자 정보가 수정되었습니다')
      setIsEditMode(false)
      onUpdate?.()
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        '사용자 정보 수정 중 오류가 발생했습니다'
      toastUtil.error(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  const handleCancel = () => {
    setFormData({
      username: user.username,
      email: user.email || '',
      nickname: user.nickname,
      phone: user.phone || '',
      is_public: user.is_public,
      is_active: user.is_active,
      location_consent: user.location_consent,
      contact_consent: user.contact_consent,
      push_notification_consent: user.push_notification_consent,
    })
    setIsEditMode(false)
  }

  const handleDeleteClick = async () => {
    const message = `정말 "${user.nickname}"(ID: ${user.id}) 사용자를 삭제하시겠습니까?\n\n삭제된 데이터는 복구할 수 없습니다.`

    if (!window.confirm(message)) return

    try {
      setIsLoading(true)
      await adminApi.deleteUser(user.id)
      toastUtil.success('사용자가 삭제되었습니다')
      onDeleteSuccess?.()
      onClose()
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        '사용자 삭제 중 오류가 발생했습니다'
      toastUtil.error(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="p-24px">
      <div className="flex gap-24px mb-24px">
        {user.profile_image && (
          <div className="flex-shrink-0">
            <img
              src={user.profile_image}
              alt="프로필"
              className={`
                w-120px
                h-120px
                rounded-12px
                object-cover
                border
                border-brandPrimary-10%
              `}
            />
          </div>
        )}
        <div className="flex-1 grid grid-cols-2 gap-16px">
          <div>
            <label className="text-style-labelS text-textColorText2 mb-4px block">
              ID
            </label>
            <div className="text-style-body2 text-textColorText2">
              {user.id}
            </div>
          </div>

          <div>
            <label className="text-style-labelS text-textColorText2 mb-4px block">
              아이디
            </label>
            {isEditMode ? (
              <AdminInput
                type="text"
                value={formData.username || ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setFormData({ ...formData, username: e.target.value })
                }
                placeholder="아이디를 입력하세요"
                wrapperClassName="mb-0"
              />
            ) : (
              <div className="text-style-body2 text-textColorText2">
                {user.username}
              </div>
            )}
          </div>

          <div>
            <label className="text-style-labelS text-textColorText2 mb-4px block">
              소셜 / 이메일
            </label>
            {isEditMode && !user.is_social_login ? (
              <AdminInput
                type="email"
                value={formData.email || ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                placeholder="이메일을 입력하세요"
                wrapperClassName="mb-0"
              />
            ) : (
              <div className="text-style-body2 text-textColorText2 break-words">
                {/* 목록과 같은 규칙 — 소셜이 주, 이메일은 있으면 곁들인다 */}
                {user.is_social_login && user.social_provider ? (
                  <span className="inline-flex items-center gap-8px flex-wrap">
                    <span
                      className={`
                        inline-flex
                        items-center
                        py-4px
                        px-12px
                        rounded-6px
                        text-12px
                        font-medium
                        ${getSocialProviderColor(user.social_provider)}
                        transition-colors
                      `}
                    >
                      {getSocialProviderName(user.social_provider)}
                    </span>
                    {user.email && (
                      <span className="text-12px text-textColorText3">
                        {user.email}
                      </span>
                    )}
                  </span>
                ) : user.email ? (
                  user.email
                ) : (
                  <span className="text-textColorText3">-</span>
                )}
              </div>
            )}
          </div>

          <div>
            <label className="text-style-labelS text-textColorText2 mb-4px block">
              닉네임
            </label>
            {isEditMode ? (
              <AdminInput
                type="text"
                value={formData.nickname || ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setFormData({ ...formData, nickname: e.target.value })
                }
                placeholder="닉네임을 입력하세요"
                wrapperClassName="mb-0"
              />
            ) : (
              <div className="text-style-body2 text-textColorText2">
                {user.nickname}
              </div>
            )}
          </div>

          <div>
            <label className="text-style-labelS text-textColorText2 mb-4px block">
              휴대폰 번호
            </label>
            {isEditMode ? (
              <AdminInput
                type="tel"
                value={formData.phone || ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setFormData({ ...formData, phone: e.target.value })
                }
                placeholder="휴대폰 번호를 입력하세요"
                wrapperClassName="mb-0"
              />
            ) : (
              <div className="text-style-body2 text-textColorText2">
                {user.phone || <span className="text-textColorText3">-</span>}
              </div>
            )}
          </div>

          {user.security_answer && (
            <div>
              <label className="text-style-labelS text-textColorText2 mb-4px block">
                보안 답변
              </label>
              <div className="text-style-body2 text-textColorText2">
                {user.security_answer}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-16px pt-16px border-t border-brandPrimary-5%">
        <div>
          <label className="text-style-labelS text-textColorText2 mb-4px block">
            프로필 공개 설정
          </label>
          {isEditMode ? (
            <select
              value={formData.is_public ? 'true' : 'false'}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  is_public: e.target.value === 'true',
                })
              }
              className={`
                w-full
                py-10px
                px-12px
                border
                border-brandPrimary-20%
                rounded-8px
                text-style-body2
                text-textColorText2
                bg-transparent
                focus:outline-none
                focus:border-brandPrimary
              `}
            >
              <option value="true">공개</option>
              <option value="false">비공개</option>
            </select>
          ) : (
            <div className="text-style-body2 text-textColorText2">
              {user.is_public ? '공개' : '비공개'}
            </div>
          )}
        </div>

        <div>
          <label className="text-style-labelS text-textColorText2 mb-4px block">
            이용약관 동의
          </label>
          <div className="text-style-body2 text-textColorText2">
            {user.agree_terms ? '동의' : '미동의'}
          </div>
        </div>

        <div>
          <label className="text-style-labelS text-textColorText2 mb-4px block">
            개인정보 처리방침 동의
          </label>
          <div className="text-style-body2 text-textColorText2">
            {user.agree_privacy ? '동의' : '미동의'}
          </div>
        </div>

        <div>
          <label className="text-style-labelS text-textColorText2 mb-4px block">
            FCM 토큰 개수
          </label>
          <div className="text-style-body2 text-textColorText2">
            {user.fcm_token_count || 0}개
          </div>
        </div>

        <div>
          <label className="text-style-labelS text-textColorText2 mb-4px block">
            탈퇴 여부
          </label>
          {isEditMode ? (
            <select
              value={formData.is_active ? 'true' : 'false'}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  is_active: e.target.value === 'true',
                })
              }
              className={`
                w-full
                py-10px
                px-12px
                border
                border-brandPrimary-20%
                rounded-8px
                text-style-body2
                ${formData.is_active ? 'text-textColorText2' : 'text-semanticError'}
                bg-transparent
                focus:outline-none
                focus:border-brandPrimary
              `}
            >
              <option value="true">활성</option>
              <option value="false">탈퇴</option>
            </select>
          ) : (
            <div
              className={`text-style-body2 ${
                user.is_active ? 'text-textColorText2' : 'text-semanticError'
              }`}
            >
              {user.is_active ? '활성' : '탈퇴'}
            </div>
          )}
        </div>
      </div>

      <div className="pt-16px border-t border-brandPrimary-5%">
        <h3 className="text-style-body1 text-textColorText2 font-semibold mb-12px">
          동의 정보
        </h3>
        <div className="grid grid-cols-2 gap-16px">
          <div>
            <label className="text-style-labelS text-textColorText2 mb-4px block">
              위치 정보 이용 동의
            </label>
            {isEditMode ? (
              <select
                value={formData.location_consent ? 'true' : 'false'}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    location_consent: e.target.value === 'true',
                  })
                }
                className={`
                  w-full
                  py-10px
                  px-12px
                  border
                  border-brandPrimary-20%
                  rounded-8px
                  text-style-body2
                  text-textColorText2
                  bg-transparent
                  focus:outline-none
                  focus:border-brandPrimary
                `}
              >
                <option value="true">동의</option>
                <option value="false">미동의</option>
              </select>
            ) : (
              <>
                <div className="text-style-body2 text-textColorText2">
                  {user.location_consent ? '동의' : '미동의'}
                </div>
                {user.location_consent_at && (
                  <div className="text-style-caption text-textColorText2 mt-4px">
                    {formatDate(user.location_consent_at)}
                  </div>
                )}
              </>
            )}
          </div>

          <div>
            <label className="text-style-labelS text-textColorText2 mb-4px block">
              연락처 연동 동의
            </label>
            {isEditMode ? (
              <select
                value={formData.contact_consent ? 'true' : 'false'}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    contact_consent: e.target.value === 'true',
                  })
                }
                className={`
                  w-full
                  py-10px
                  px-12px
                  border
                  border-brandPrimary-20%
                  rounded-8px
                  text-style-body2
                  text-textColorText2
                  bg-transparent
                  focus:outline-none
                  focus:border-brandPrimary
                `}
              >
                <option value="true">동의</option>
                <option value="false">미동의</option>
              </select>
            ) : (
              <>
                <div className="text-style-body2 text-textColorText2">
                  {user.contact_consent ? '동의' : '미동의'}
                </div>
                {user.contact_consent_at && (
                  <div className="text-style-caption text-textColorText2 mt-4px">
                    {formatDate(user.contact_consent_at)}
                  </div>
                )}
              </>
            )}
          </div>

          <div>
            <label className="text-style-labelS text-textColorText2 mb-4px block">
              광고성 푸시 알림 동의
            </label>
            {isEditMode ? (
              <select
                value={formData.push_notification_consent ? 'true' : 'false'}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    push_notification_consent: e.target.value === 'true',
                  })
                }
                className={`
                  w-full
                  py-10px
                  px-12px
                  border
                  border-brandPrimary-20%
                  rounded-8px
                  text-style-body2
                  text-textColorText2
                  bg-transparent
                  focus:outline-none
                  focus:border-brandPrimary
                `}
              >
                <option value="true">동의</option>
                <option value="false">미동의</option>
              </select>
            ) : (
              <>
                <div className="text-style-body2 text-textColorText2">
                  {user.push_notification_consent ? '동의' : '미동의'}
                </div>
                {user.push_notification_consent_at && (
                  <div className="text-style-caption text-textColorText2 mt-4px">
                    {formatDate(user.push_notification_consent_at)}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-16px pt-16px border-t border-brandPrimary-5%">
        <div>
          <label className="text-style-labelS text-textColorText2 mb-4px block">
            가입일
          </label>
          <div className="text-style-body2 text-textColorText2">
            {formatDate(user.created_at)}
          </div>
        </div>

        <div>
          <label className="text-style-labelS text-textColorText2 mb-4px block">
            수정일
          </label>
          <div className="text-style-body2 text-textColorText2">
            {formatDate(user.updated_at)}
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-12px mt-24px pt-24px border-t border-brandPrimary-5%">
        {isEditMode ? (
          <>
            <button
              onClick={handleCancel}
              className={`
                py-10px
                px-20px
                border
                border-brandPrimary-20%
                text-textColorText2
                rounded-8px
                text-style-button2
                hover:bg-backgroundColorBg2
                transition-colors
              `}
            >
              취소
            </button>
            <button
              onClick={handleSubmit}
              disabled={!formData.nickname?.trim() || isLoading}
              className={`
                py-10px
                px-20px
                bg-4B3621-80%
                text-white
                rounded-8px
                text-style-button2
                hover:bg-4B3621-80%-70%
                transition-colors
                disabled:bg-4B3621-80%-10%
                disabled:text-textColorText3
                disabled:cursor-not-allowed
                w-auto
                min-w-[120px]
              `}
            >
              {isLoading ? '수정 중...' : '수정 완료'}
            </button>
          </>
        ) : (
          <>
            <button
              onClick={handleDeleteClick}
              disabled={isLoading}
              className={`
                py-10px
                px-20px
                bg-semanticError
                text-white
                rounded-8px
                text-style-button2
                hover:bg-semanticError-70%
                transition-colors
                disabled:bg-semanticError-10%
                disabled:text-textColorText3
                disabled:cursor-not-allowed
              `}
            >
              삭제
            </button>
            <button
              onClick={() => setIsEditMode(true)}
              className={`
                py-10px
                px-20px
                bg-4B3621-80%
                text-white
                rounded-8px
                text-style-button2
                hover:bg-4B3621-80%-70%
                transition-colors
              `}
            >
              수정
            </button>
            <button
              onClick={onClose}
              className={`
                py-10px
                px-20px
                border
                border-brandPrimary-20%
                text-textColorText2
                rounded-8px
                text-style-button2
                hover:bg-backgroundColorBg2
                transition-colors
              `}
            >
              닫기
            </button>
          </>
        )}
      </div>
    </div>
  )
}

export default AdminUserDetailModal
